/**
 * Copyright (c) HashiCorp, Inc.
 */

import { hash } from '@ember/helper';
import { gt } from 'ember-truth-helpers';
import { HdsApplicationState } from '@hashicorp/design-system-components/components';

import ListContentsGeneric from './list/contents/generic.gts';
import ListContentsEmpty from './list/contents/empty.gts';
import Pagination from './list/pagination.gts';
import ListItemService from './list-item/service.gts';
import ListItemServiceInstance from './list-item/service-instance.gts';
import CutFilterBar from './filter-bar.gts';
import ListItem from './list-item.gts';
import './list.scss';

import type { TOC } from '@ember/component/template-only';
import type { ComponentLike } from '@glint/template';
import type { HdsApplicationStateSignature } from '@hashicorp/design-system-components/components/hds/application-state/index';
import type { CutFilterBarSignature } from './filter-bar.gts';
import type { ListItemSignature } from './list-item.gts';
import type { PaginationSignature } from './list/pagination.gts';
import type { ListContentsEmptySignature } from './list/contents/empty.gts';
import type { ListItemServiceSignature } from './list-item/service.gts';
import type { ListContentsGenericSignature } from './list/contents/generic.gts';

export interface CutListSignature {
  Args: {
    items: unknown[];
  };
  Blocks: {
    default: [
      {
        FilterBar?: ComponentLike<CutFilterBarSignature>;
        item?: unknown;
        Generic?: ComponentLike<ListContentsGenericSignature>;
        ListItem?: ComponentLike<ListItemSignature>;
        ServiceListItem?: ComponentLike<ListItemServiceSignature>;
        ServiceInstanceListItem?: ComponentLike<ListItemServiceInstance>;
        EmptyGeneric?: ComponentLike<ListContentsEmptySignature>;
        EmptyAppState?: ComponentLike<HdsApplicationStateSignature>;
        Pagination?: ComponentLike<PaginationSignature>;
      },
    ];
  };
  Element: HTMLDivElement;
}

/**
 *
 * @class CutList
 * @example `Cut::List` provides a simple layout for a common list UI across Consul apps. It exposes an optional FilterBar,
    a block for your list item specification, empty states, and an optional pagination component.

    ```
    <Cut::List @items={{this.items}} as |L|>
     <L.FilterBar ... />
     <L.ListItem ... />
     <L.EmptyAppState ... />
     <L.Pagination ... />
    </Cut::List>
    ```
 */
const CutList: TOC<CutListSignature> = <template>
  <div class="cut-list" ...attributes>
    <div class="cut-list--group">
      {{yield (hash FilterBar=CutFilterBar)}}
      <div class="cut-list-contents">
        {{#if (gt @items.length 0)}}
          <ul class="cut-list-items">
            {{#each @items as |item|}}
              {{yield
                (hash
                  item=item
                  Generic=ListContentsGeneric
                  ListItem=ListItem
                  ServiceListItem=ListItemService
                  ServiceInstanceListItem=ListItemServiceInstance
                )
              }}
            {{/each}}
          </ul>
        {{else}}
          <div class="cut-list--empty">
            {{yield
              (hash
                EmptyGeneric=ListContentsEmpty EmptyAppState=HdsApplicationState
              )
            }}
          </div>
        {{/if}}
      </div>
    </div>

    {{#if (gt @items.length 0)}}
      {{yield (hash Pagination=Pagination)}}
    {{/if}}
  </div>
</template>;

export default CutList;
